import { useCallback, useEffect, useRef, useState } from "react";

export type ReconciliationQueueStatus =
  | "pending"
  | "submitting"
  | "waiting"
  | "running"
  | "completed"
  | "failed";

export interface ReconciliationQueueGroup {
  id: string;
  name: string;
}

export interface ReconciliationQueueItem extends ReconciliationQueueGroup {
  status: ReconciliationQueueStatus;
  error?: string;
}

interface UseReconciliationQueueParams {
  working: boolean;
  executeGroup: (groupId: string) => Promise<unknown>;
  onCompleted?: (groupId: string) => void;
}

interface ActiveRunState {
  requestSettled: boolean;
  engineStarted: boolean;
  engineFinished: boolean;
}

const EMPTY_ACTIVE_RUN: ActiveRunState = {
  requestSettled: false,
  engineStarted: false,
  engineFinished: false,
};

export function useReconciliationQueue({
  working,
  executeGroup,
  onCompleted,
}: UseReconciliationQueueParams) {
  const [items, setItems] = useState<ReconciliationQueueItem[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const itemsRef = useRef<ReconciliationQueueItem[]>([]);
  const activeIndexRef = useRef<number | null>(null);
  const activeRunRef = useRef<ActiveRunState>({ ...EMPTY_ACTIVE_RUN });
  const generationRef = useRef(0);
  const submittedKeyRef = useRef<string | undefined>(undefined);

  const updateItem = useCallback(
    (index: number, update: Partial<ReconciliationQueueItem>) => {
      setItems((currentItems) => {
        const nextItems = currentItems.map((item, itemIndex) =>
          itemIndex === index ? { ...item, ...update } : item,
        );
        itemsRef.current = nextItems;
        return nextItems;
      });
    },
    [],
  );

  const moveToNext = useCallback((index: number) => {
    const nextIndex = index + 1;
    activeRunRef.current = { ...EMPTY_ACTIVE_RUN };

    if (nextIndex < itemsRef.current.length) {
      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);
      return;
    }

    activeIndexRef.current = null;
    setActiveIndex(null);
  }, []);

  const completeCurrent = useCallback(() => {
    const index = activeIndexRef.current;
    if (index === null) return;

    updateItem(index, { status: "completed", error: undefined });
    onCompleted?.(itemsRef.current[index].id);
    moveToNext(index);
  }, [moveToNext, onCompleted, updateItem]);

  const failCurrent = useCallback(
    (error: unknown) => {
      const index = activeIndexRef.current;
      if (index === null) return;

      updateItem(index, {
        status: "failed",
        error:
          error instanceof Error
            ? error.message
            : "No fue posible iniciar la conciliación.",
      });
      moveToNext(index);
    },
    [moveToNext, updateItem],
  );

  useEffect(() => {
    if (activeIndex === null) return;

    const generation = generationRef.current;
    const submittedKey = `${generation}:${activeIndex}`;
    if (submittedKeyRef.current === submittedKey) return;
    submittedKeyRef.current = submittedKey;

    const item = itemsRef.current[activeIndex];
    if (!item) return;

    activeRunRef.current = { ...EMPTY_ACTIVE_RUN };
    updateItem(activeIndex, { status: "submitting", error: undefined });

    void executeGroup(item.id)
      .then(() => {
        if (
          generation !== generationRef.current ||
          activeIndexRef.current !== activeIndex
        ) {
          return;
        }

        activeRunRef.current.requestSettled = true;

        if (activeRunRef.current.engineFinished) {
          completeCurrent();
          return;
        }

        if (!activeRunRef.current.engineStarted) {
          updateItem(activeIndex, { status: "waiting" });
        }
      })
      .catch((error: unknown) => {
        if (
          generation !== generationRef.current ||
          activeIndexRef.current !== activeIndex
        ) {
          return;
        }

        if (activeRunRef.current.engineStarted) {
          activeRunRef.current.requestSettled = true;
          if (activeRunRef.current.engineFinished) {
            completeCurrent();
          }
          return;
        }

        failCurrent(error);
      });
  }, [activeIndex, completeCurrent, executeGroup, failCurrent, updateItem]);

  useEffect(() => {
    const index = activeIndexRef.current;
    if (index === null) return;

    if (working) {
      activeRunRef.current.engineStarted = true;
      updateItem(index, { status: "running" });
      return;
    }

    if (!activeRunRef.current.engineStarted) return;

    activeRunRef.current.engineFinished = true;
    if (activeRunRef.current.requestSettled) {
      completeCurrent();
    }
  }, [completeCurrent, updateItem, working]);

  const start = useCallback((groups: ReconciliationQueueGroup[]) => {
    if (groups.length === 0 || activeIndexRef.current !== null) return;

    generationRef.current += 1;
    submittedKeyRef.current = undefined;
    const nextItems = groups.map((group) => ({
      ...group,
      status: "pending" as const,
    }));

    itemsRef.current = nextItems;
    activeIndexRef.current = 0;
    activeRunRef.current = { ...EMPTY_ACTIVE_RUN };
    setItems(nextItems);
    setActiveIndex(0);
  }, []);

  const reset = useCallback(() => {
    if (activeIndexRef.current !== null) return;

    generationRef.current += 1;
    submittedKeyRef.current = undefined;
    itemsRef.current = [];
    setItems([]);
  }, []);

  const completedCount = items.filter(
    ({ status }) => status === "completed",
  ).length;
  const failedCount = items.filter(({ status }) => status === "failed").length;

  return {
    items,
    activeIndex,
    isRunning: activeIndex !== null,
    completedCount,
    failedCount,
    processedCount: completedCount + failedCount,
    start,
    reset,
  };
}
